import { FormField, type UseFormReturn } from "@gigpro/ui/Form";
import type { SkillListItemReadonly } from "@sidegigllc/api/client";
import { useRequirementForWorksList } from "@sidegigllc/api/client";
import { keepPreviousData } from "@tanstack/react-query";
import { EmojiLoader } from "components/Loader/emoji";
import { RequirementGroups } from "components/RequirementGroups";
import type { Pro } from "domains/postGig/providers/PostGigData/types";
import { useWatch } from "react-hook-form";

export interface RequirementsProps {
  form: UseFormReturn<Pro, Array<SkillListItemReadonly>, Pro>;
  /** Server-side filter (CORE-6456): when set, only requirements in this category are listed. */
  category?: string;
}

export function Requirements({ form, category }: Readonly<RequirementsProps>) {
  const [skillId, skillName] = useWatch({ control: form.control, name: ["skill_id", "skill_name"] });
  const { data: requirements } = useRequirementForWorksList(
    { skill_id: skillId, ...(category ? { category } : {}) },
    { query: { staleTime: Number.POSITIVE_INFINITY, placeholderData: keepPreviousData } },
  );

  if (!requirements) {
    return <EmojiLoader />;
  }

  return (
    <FormField
      control={form.control}
      name="requirements"
      render={({ field }) => (
        <RequirementGroups
          requirements={requirements}
          selectedIds={field.value?.map((r) => r.id) ?? []}
          onValueChange={(ids) => field.onChange(requirements?.filter(({ id }) => ids.includes(String(id))))}
          skillId={skillId}
          skillName={skillName}
          onRequirementCreated={(requirement) => field.onChange([...(field.value ?? []), requirement])}
        />
      )}
    />
  );
}
